/**
 * 생성된 맵을 눈으로 보는 창.
 *
 * 결과가 이상할 때 어디가 틀렸는지 알 방법이 없으면 파라미터를 감으로 흔들게 된다.
 * 노멀맵이 평평한지, 마스크가 배경을 먹었는지, 깊이가 뒤집혔는지 — 여기서 보인다.
 * 디버깅 도구가 아니라 제품의 일부다.
 */

import { useEffect, useMemo, useRef } from 'react';
import type { Field, Raster } from '../core/types.ts';
import { useForgeStore } from '../state/store.ts';
import { fieldToRaster } from '../core/imaging/raster.ts';

const THUMB = 192;

/**
 * CPU 상자 평균 축소. 처음 판은 원본 크기 OffscreenCanvas 를 만들어 drawImage 로 줄였는데,
 * 8뷰 아틀라스(3096²)를 렌더마다 새 캔버스에 올리자 GPU 캔버스 메모리가 차서 탭이 통째로
 * 멈췄다(2026-09-04 실측 — 4뷰·8뷰 무깎기는 살고 8뷰+깊이만 죽는 이유가 이것이었다).
 * 상자 평균이면 모아레도 없고 GPU 메모리도 안 쓴다.
 */
function downsample(raster: Raster, w: number, h: number): ImageData {
  const out = new ImageData(w, h);
  const sx = raster.width / w, sy = raster.height / h;
  for (let y = 0; y < h; y++) {
    const y0 = Math.floor(y * sy), y1 = Math.max(y0 + 1, Math.floor((y + 1) * sy));
    for (let x = 0; x < w; x++) {
      const x0 = Math.floor(x * sx), x1 = Math.max(x0 + 1, Math.floor((x + 1) * sx));
      let r = 0, g = 0, b = 0, a = 0, n = 0;
      // 큰 블록은 최대 4×4 표본만 — 3096² 를 전부 훑을 필요는 없다.
      const stepX = Math.max(1, Math.floor((x1 - x0) / 4)), stepY = Math.max(1, Math.floor((y1 - y0) / 4));
      for (let yy = y0; yy < y1; yy += stepY) {
        for (let xx = x0; xx < x1; xx += stepX) {
          const p = (yy * raster.width + xx) * 4;
          r += raster.data[p]!; g += raster.data[p + 1]!; b += raster.data[p + 2]!; a += raster.data[p + 3]!; n++;
        }
      }
      const q = (y * w + x) * 4;
      out.data[q] = r / n; out.data[q + 1] = g / n; out.data[q + 2] = b / n; out.data[q + 3] = a / n;
    }
  }
  return out;
}

function drawRaster(canvas: HTMLCanvasElement, raster: Raster) {
  const scale = THUMB / Math.max(raster.width, raster.height);
  const w = Math.max(1, Math.round(raster.width * scale));
  const h = Math.max(1, Math.round(raster.height * scale));
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.putImageData(downsample(raster, w, h), 0, 0);
}

function MapCard({ title, note, raster }: { title: string; note?: string; raster: Raster | null }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (ref.current && raster) drawRaster(ref.current, raster);
  }, [raster, title]);
  if (!raster) return null;
  return (
    <div className="map-card">
      <div className="label">
        <span>{title}</span>
        {note && <span>{note}</span>}
      </div>
      <canvas ref={ref} />
    </div>
  );
}

function fieldCard(field: Field | null | undefined): Raster | null {
  return field ? fieldToRaster(field) : null;
}

export function MapInspector() {
  const analysis = useForgeStore((s) => s.analysis);
  const maps = useForgeStore((s) => s.maps);
  const depth = useForgeStore((s) => s.depth);
  const mesh = useForgeStore((s) => s.mesh);
  const timings = useForgeStore((s) => s.timings);
  // 렌더마다 새 Raster 를 만들면 MapCard 의 effect 가 매번 다시 그린다 — 분석·깊이가 바뀔 때만.
  const maskRaster = useMemo(() => (analysis ? fieldCard(analysis.mask) : null), [analysis]);
  const depthRaster = useMemo(() => (depth ? fieldCard(depth.field) : null), [depth]);

  if (!analysis) {
    return (
      <aside className="inspector">
        <div className="section">
          <h2>맵</h2>
          <p className="hint">이미지를 넣으면 여기에서 생성된 맵을 확인할 수 있습니다.</p>
        </div>
      </aside>
    );
  }

  const stats = analysis.stats;
  const total = Object.values(timings).reduce((a, b) => a + b, 0);

  return (
    <aside className="inspector">
      <div className="section">
        <h2>팔레트</h2>
        <div className="swatches">
          {analysis.palette.slice(0, 6).map((p, i) => (
            <div
              key={i}
              className="swatch"
              style={{
                background: `rgb(${p.rgb[0]}, ${p.rgb[1]}, ${p.rgb[2]})`,
                flex: Math.max(0.4, p.weight * 6),
              }}
              title={`rgb(${p.rgb.join(', ')}) · ${(p.weight * 100).toFixed(1)}%`}
            />
          ))}
        </div>
      </div>

      <div className="section">
        <h2>맵</h2>
        <MapCard title="Albedo" note="디라이팅 후" raster={maps?.albedo ?? null} />
        <MapCard title="Normal" note="접선공간" raster={maps?.normal ?? null} />
        <MapCard title="ORM" note="AO·러프·메탈" raster={maps?.orm ?? null} />
        {depth && <MapCard title="Depth" note={depth.backend === 'onnx' ? '신경망' : '휴리스틱'} raster={depthRaster} />}
        <MapCard title="Mask" note={analysis.maskSource} raster={maskRaster} />
      </div>

      <div className="section">
        <h2>수치</h2>
        <dl className="stat-grid">
          <dt>정점</dt><dd>{mesh?.vertexCount.toLocaleString() ?? '—'}</dd>
          <dt>삼각형</dt><dd>{mesh?.triangleCount.toLocaleString() ?? '—'}</dd>
          <dt>평균 휘도</dt><dd>{stats.meanLuma.toFixed(3)}</dd>
          <dt>대비</dt><dd>{stats.contrast.toFixed(3)}</dd>
          <dt>채도</dt><dd>{stats.meanSaturation.toFixed(3)}</dd>
          <dt>디테일</dt><dd>{stats.detail.toFixed(4)}</dd>
          <dt>스페큘러</dt><dd>{(stats.specularRatio * 100).toFixed(1)}%</dd>
          <dt>총 소요</dt><dd>{Math.round(total)}ms</dd>
        </dl>
      </div>
    </aside>
  );
}
